import { useEffect, useCallback } from 'react';
import { useHARStore } from '@/stores/har-store';

// Helper to read file as text
const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

export interface UseKeyboardNavigationOptions {
  onShowShortcuts?: () => void;
}

export function useKeyboardNavigation(options?: UseKeyboardNavigationOptions) {
  const {
    filteredEntries,
    selectedEntry,
    selectEntry,
    sidebarOpen,
    setSidebarOpen,
  } = useHARStore();

  const { addTab } = useHARStore.getState();

  const handleKeyDown = useCallback(async (e: KeyboardEvent) => {
    // Don't handle if user is typing in an input
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    // ? key - show keyboard shortcuts modal
    if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      options?.onShowShortcuts?.();
      return;
    }

    // Escape - close sidebar
    if (e.key === 'Escape' && sidebarOpen) {
      e.preventDefault();
      setSidebarOpen(false);
      return;
    }

    // Cmd/Ctrl + O - open file dialog
    // Check for both lowercase and uppercase 'O' for cross-browser compatibility
    if ((e.metaKey || e.ctrlKey) && (e.key === 'o' || e.key === 'O')) {
      e.preventDefault();
      // Create a temporary file input with proper accept attribute
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.har,.json';
      fileInput.style.display = 'none';
      document.body.appendChild(fileInput);
      
      // Handle file selection
      fileInput.onchange = async (event) => {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const file = target.files[0];
          try {
            const content = await readFileAsText(file);
            addTab(content, file.name);
          } catch (err) {
            console.error('Failed to read file:', err);
          }
        }
        document.body.removeChild(fileInput);
      };
      
      // Handle cancel
      fileInput.addEventListener('cancel', () => {
        document.body.removeChild(fileInput);
      });
      
      fileInput.click();
      return;
    }

    // No entries to navigate
    if (filteredEntries.length === 0) return;

    const currentIndex = selectedEntry
      ? filteredEntries.findIndex(e => e._id === selectedEntry._id)
      : -1;

    // Arrow Up - previous entry
    if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      if (currentIndex > 0) {
        selectEntry(filteredEntries[currentIndex - 1]);
      } else if (currentIndex === -1) {
        selectEntry(filteredEntries[filteredEntries.length - 1]);
      }
      return;
    }

    // Arrow Down - next entry
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      if (currentIndex < filteredEntries.length - 1) {
        selectEntry(filteredEntries[currentIndex + 1]);
      } else if (currentIndex === -1) {
        selectEntry(filteredEntries[0]);
      }
      return;
    }

    // Enter - open detail sidebar (if entry selected but sidebar closed)
    if (e.key === 'Enter' && selectedEntry && !sidebarOpen) {
      e.preventDefault();
      setSidebarOpen(true);
      return;
    }

    // Home - first entry
    if (e.key === 'Home') {
      e.preventDefault();
      selectEntry(filteredEntries[0]);
      return;
    }

    // End - last entry
    if (e.key === 'End') {
      e.preventDefault();
      selectEntry(filteredEntries[filteredEntries.length - 1]);
      return;
    }
  }, [filteredEntries, selectedEntry, selectEntry, sidebarOpen, setSidebarOpen, options]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
